import { readXlsxFile, removeNullFields, Tab, useLoading } from "best-common-react";
import * as Papa from "papaparse";
import React, { useContext, useEffect, useState } from "react";
import { UploadConstants } from "../constants/UploadConstants";
import { multiFileUpload, singleFileUpload, uploadStatus } from "../httpClients/ClipsApi";
import { MultiFileUploaData, UploadBatch } from "../types/Upload";
import { ClipsMetadataDTO } from "../types/Video";
import { convertMetadata } from "../utils/MetadataUtils";
import { useAuth } from "./AuthContext";

type UploadContextType = {
  uploadStatuses: UploadBatch[];
  metadata: ClipsMetadataDTO;
  file: File;
  fileContent: string;
  canSave: boolean;
  straightToFinal: boolean;
  multiFile: File;
  setMultiFileData: (value: File[]) => void;
  setMetadata: (value: ClipsMetadataDTO) => void;
  setFileData: (value: File[]) => void;
  setStraightToFinal: (value: boolean) => void;
  upload: () => Promise<any>;
  setActiveTab: (value: Tab) => void;
};

const UploadContext = React.createContext<UploadContextType | undefined>(undefined);

const UploadProvider = ({ children }) => {
  const { userInfo } = useAuth();
  const { setLoading } = useLoading();
  const defaultMetadata: ClipsMetadataDTO = {
    firstName: "",
    lastName: "",
    uploadDate: new Date(),
    source: userInfo?.org?.name,
    org: userInfo?.org?.name,
  };
  const [activeTab, setActiveTab] = useState<Tab | undefined>(undefined);
  const [metadata, setMetadata] = useState<ClipsMetadataDTO>(defaultMetadata);
  const [multiFile, setMultiFile] = useState<File | undefined>(undefined);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [fileContent, setFileContent] = useState<string>("");
  const [multiFileUploadData, setMultiFileUploadData] = useState<ClipsMetadataDTO[]>([]);
  const [uploadStatuses, setUploadStatuses] = useState<UploadBatch[]>([]);
  const [straightToFinal, setStraightToFinal] = useState<boolean>(false);
  const [canSave, setCanSave] = useState<boolean>(false);

  const resetData = () => {
    setMultiFile(undefined);
    setFile(undefined);
    setMultiFileUploadData([]);
    setStraightToFinal(false);
    setFileContent("");
    setMetadata(defaultMetadata);
  };

  const singleFileUploadCall = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append(
        "params",
        JSON.stringify({
          fileName: file?.name,
          fileSize: file?.size,
          metadata: metadata,
        }),
      );
      formData.append("file", file);
      await singleFileUpload(formData, straightToFinal);
      resetData();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const multiFileUploadCall = async () => {
    try {
      setLoading(true);
      const uploadData = {
        metadata: multiFileUploadData.map((d) => ({
          ...d,
          org: userInfo?.org?.name,
          tags: !!d.tags && !Array.isArray(d.tags) ? (d.tags as string).split(",") : d.tags,
        })),
        straightToFinal: straightToFinal,
      };
      await multiFileUpload(uploadData);
      resetData();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const upload = async () => {
    if (activeTab) {
      if (activeTab.name === UploadConstants.Tabs.Single) {
        return singleFileUploadCall();
      } else if (activeTab.name === UploadConstants.Tabs.Multi) {
        return multiFileUploadCall();
      }
    }
  };

  const setFileData = (files) => {
    if (files.length) {
      const video = files[0];
      setFile(video);
      //todo: explore reading video file to use on a video preview
      // const reader = new FileReader();
      // reader.onload = () => {
      //   console.debug("File read: " + reader.result);
      //   setFileContent(reader.result);
      // };
      // reader.readAsText(video);
    } else {
      setFile(undefined);
    }
  };

  const fetchUploadStatus = async () => {
    const result: UploadBatch[] = await uploadStatus();
    setUploadStatuses(result);
  };

  const parseData = (data: MultiFileUploaData[]) => {
    const fileData: MultiFileUploaData[] = !!data ? data.map((d) => removeNullFields(d) as MultiFileUploaData) : [];
    setMultiFileUploadData(convertMetadata(fileData));
  };

  const parseCsvFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      console.debug("File read: " + reader.result);
      setFileContent(reader.result as string);
      const tempCsvData: MultiFileUploaData[] = Papa.parse(reader.result, { header: true }).data;
      parseData(tempCsvData);
    };
    reader.readAsText(file);
  };

  const parseExcelFile = async (file: File) => {
    const data = await readXlsxFile(file);
    const uploadData: MultiFileUploaData[] = data[Object.keys(data)[0]] as MultiFileUploaData[];
    parseData(uploadData);
  };

  const setMultiFileData = (files: File[]) => {
    if (files.length) {
      const multiFileData = files[0];
      setMultiFile(multiFileData);
      if (multiFileData.name.endsWith(".csv")) {
        parseCsvFile(multiFileData);
      } else if (multiFileData.name.endsWith(".xlsx") || multiFileData.name.endsWith(".xls")) {
        parseExcelFile(multiFileData);
      }
    } else {
      setMultiFile(undefined);
      setMultiFileUploadData([]);
    }
  };

  useEffect(() => {
    if (activeTab?.name === UploadConstants.Tabs.Multi) {
      const interval = setInterval(() => {
        void fetchUploadStatus();
      }, 5000);
      return () => clearInterval(interval);
    } else {
      return () => {
        console.debug("ignore");
      };
    }
  }, [activeTab]);

  useEffect(() => {
    let canUserSave = false;
    if (!!activeTab) {
      if (activeTab.name === UploadConstants.Tabs.Single) {
        canUserSave =
          !!metadata.firstName &&
          !!metadata.lastName &&
          !!metadata.playerStatus &&
          !!file &&
          !!Object.keys(file).length;
      } else if (activeTab.name === UploadConstants.Tabs.Multi) {
        canUserSave = !!multiFileUploadData.length;
      }
    }
    setCanSave(canUserSave);
  }, [activeTab, metadata, file, multiFileUploadData]);

  return (
    <UploadContext.Provider
      value={{
        uploadStatuses,
        metadata,
        file,
        fileContent,
        canSave,
        straightToFinal,
        multiFile,
        setMultiFileData,
        setMetadata,
        setFileData,
        setStraightToFinal,
        upload,
        setActiveTab,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

const useUploads = () => {
  const context: UploadContextType | undefined = useContext<UploadContextType | undefined>(UploadContext);
  if (context === undefined) {
    throw new Error(`useUploads must be used within a UploadsProvider`);
  }
  return context;
};

export { UploadProvider, useUploads };
